// Web → Log změn webu.
// Kdy se co na fitway40.cz změnilo, co se od toho čekalo a jestli to zabralo.
// Data z /api/web-zmeny (Netlify Blobs, izolované úložiště, stejný vzor jako mapa webu).
//
// Proč to existuje: v Analytice webu se čísla filtrují podle data. Bez zápisu,
// co přesně se změnilo a které číslo se tím mělo hnout, se zpětně nedá říct,
// jestli změna zabrala. Git log říká jen co se změnilo v kódu, ne co jsme čekali.
//
// Zobrazení (25.8.2026): přehledné řádky jako v Klientech, klik rozbalí detail
// jako v Hlášení. Editace je vždy jen u jednoho záznamu, ne globální režim,
// jinak se v dvanácti rozbalených formulářích nedá vyznat.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  History, Loader2, Plus, Trash2, Save, Pencil, X, Search, ChevronDown, ChevronUp,
  Sparkles, Gauge, AlertTriangle, Ban, CalendarClock, Target, MapPin,
  TrendingUp, TrendingDown, Minus, BarChart3,
} from "lucide-react";
import { apiFetch } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, ErrorNote } from "../../components/ui.jsx";

// Typ záznamu. Log neobsahuje jen úpravy webu: patří sem i změny způsobu
// měření a incidenty, protože obojí posouvá čísla, aniž by se web změnil.
const TYPY = {
  zmena:      { label: "Změna webu", icon: Sparkles,      chip: "bg-brand-100 text-brand-700 border-brand-200", pruh: "bg-brand-500" },
  mereni:     { label: "Měření",     icon: Gauge,         chip: "bg-sky-100 text-sky-700 border-sky-200",       pruh: "bg-sky-500" },
  incident:   { label: "Incident",   icon: AlertTriangle, chip: "bg-rose-100 text-rose-700 border-rose-200",    pruh: "bg-rose-500" },
  rozhodnuti: { label: "Rozhodnutí", icon: Ban,           chip: "bg-slate-100 text-slate-600 border-slate-200", pruh: "bg-slate-400" },
};

const STAVY = {
  ceka:             { label: "Čeká",              chip: "bg-amber-100 text-amber-700 border-amber-200" },
  zlepseni:         { label: "Zlepšení",          chip: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  "beze-zmeny":     { label: "Beze změny",        chip: "bg-slate-100 text-slate-600 border-slate-200" },
  zhorseni:         { label: "Zhoršení",          chip: "bg-rose-100 text-rose-700 border-rose-200" },
  nevyhodnotitelne: { label: "Nevyhodnotitelné",  chip: "bg-slate-100 text-slate-500 border-slate-200" },
  hotovo:           { label: "Hotovo",            chip: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const POLE = [
  { key: "datum",        label: "Datum nasazení",           type: "date" },
  { key: "typ",          label: "Typ",                      type: "select", options: Object.keys(TYPY),  popis: (v) => TYPY[v].label },
  { key: "nadpis",       label: "Nadpis",                   type: "text",   sirka: "full" },
  { key: "kde",          label: "Kde",                      type: "text",   sirka: "full" },
  { key: "co",           label: "Co se změnilo",            type: "textarea" },
  { key: "proc",         label: "Proč",                     type: "textarea" },
  { key: "metrika",      label: "Které číslo se má hnout",  type: "textarea" },
  { key: "metrikaKlic",  label: "Sledovaná metrika (pro automatický výpočet)", type: "metrika" },
  { key: "vyhodnotitOd", label: "Vyhodnotit od",            type: "date" },
  { key: "stav",         label: "Stav",                     type: "select", options: Object.keys(STAVY), popis: (v) => STAVY[v].label },
  { key: "vysledek",     label: "Výsledek",                 type: "textarea" },
  { key: "poznamka",     label: "Poznámka / dopad na data", type: "textarea" },
];

function dnesISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtDatum(iso) {
  if (!iso) return "";
  const [r, m, d] = String(iso).split("-");
  if (!r || !m || !d) return iso;
  return `${Number(d)}. ${Number(m)}. ${r}`;
}


// Záznam, který čeká na vyhodnocení a datum už uplynulo. Přesně tohle je
// důvod, proč log existuje: bez připomenutí se nevyhodnotí nikdy.
function jeCasVyhodnotit(z) {
  return z.stav === "ceka" && z.vyhodnotitOd && z.vyhodnotitOd <= dnesISO();
}

function prazdnyZaznam() {
  return {
    id: `z_${Date.now()}`,
    datum: dnesISO(),
    typ: "zmena",
    nadpis: "",
    kde: "",
    co: "",
    proc: "",
    metrika: "",
    metrikaKlic: "",
    vyhodnotitOd: "",
    stav: "ceka",
    vysledek: "",
    poznamka: "",
  };
}

/**
 * Čísla před nasazením a po něm.
 *
 * Nevyhodnocuje samo. Předloží jen obě hodnoty a velikost vzorku, ať se
 * nemusí klikat v Analytice. Rozhodnutí, jestli změna zabrala, dělá člověk,
 * protože ve stejném období mohla běžet jiná reklama nebo jiná změna.
 */
function CislaZmeny({ zaznam, onStav, saving }) {
  const [data, setData] = useState(null);
  const [nacita, setNacita] = useState(false);

  useEffect(() => {
    if (!zaznam.metrikaKlic || !zaznam.datum) { setData(null); return; }
    let zrusen = false;
    setNacita(true);
    apiFetch(`/api/web-zmeny-cisla?klic=${encodeURIComponent(zaznam.metrikaKlic)}&datum=${encodeURIComponent(zaznam.datum)}`)
      .then((r) => r.json())
      .then((j) => { if (!zrusen) setData(j); })
      .catch(() => { if (!zrusen) setData(null); })
      .finally(() => { if (!zrusen) setNacita(false); });
    return () => { zrusen = true; };
  }, [zaznam.metrikaKlic, zaznam.datum]);

  if (!zaznam.metrikaKlic) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 px-3.5 py-3 text-sm text-slate-500">
        Bez vybrané metriky se čísla nedají spočítat. Doplň ji přes Upravit.
      </div>
    );
  }
  if (nacita) {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-3.5 py-3 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Počítám čísla
      </div>
    );
  }
  if (!data) return null;
  if (!data.ok || data.duvod) {
    return (
      <div className="rounded-xl bg-slate-50 px-3.5 py-3 text-sm text-slate-500">{data.duvod || "Čísla se nepodařilo spočítat."}</div>
    );
  }

  const fmt = (v) => {
    if (!v || v.hodnota == null) return "bez dat";
    return v.procenta ? `${v.hodnota.toFixed(1)} %` : String(v.hodnota);
  };
  const detail = (v) => (v && v.procenta && v.jmenovatel ? `${v.citatel} z ${v.jmenovatel}` : "");
  const r = data.rozdil;
  const Sipka = r == null ? Minus : r > 0.01 ? TrendingUp : r < -0.01 ? TrendingDown : Minus;
  const barva = r == null || Math.abs(r) <= 0.01 ? "text-slate-500" : r > 0 ? "text-emerald-600" : "text-rose-600";

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <BarChart3 className="w-4 h-4 text-brand-500" /> {data.label}
        <Delic />
        <span className="font-medium normal-case tracking-normal text-slate-400">{data.dnu} dní před a po</span>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div>
          <div className="text-xs text-slate-500">Před nasazením</div>
          <div className="text-lg font-semibold text-slate-800">{fmt(data.pred)}</div>
          <div className="text-xs text-slate-400">{detail(data.pred)}</div>
        </div>
        <span aria-hidden className="text-slate-300">→</span>
        <div>
          <div className="text-xs text-slate-500">Po nasazení</div>
          <div className="text-lg font-semibold text-slate-900">{fmt(data.po)}</div>
          <div className="text-xs text-slate-400">{detail(data.po)}</div>
        </div>
        {r != null && (
          <div className={`flex items-center gap-1.5 text-sm font-semibold ${barva}`}>
            <Sipka className="w-4 h-4" />
            {r > 0 ? "+" : ""}{r.toFixed(1)}{data.pred?.procenta ? " p. b." : ""}
          </div>
        )}
      </div>

      {data.malyVzorek && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Vzorek je pod 100 návštěvnic. Rozdíl může být jen náhoda, radši počkej na víc dat.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Vyhodnotit jako</span>
        {["zlepseni", "beze-zmeny", "zhorseni", "nevyhodnotitelne"].map((k) => (
          <button
            key={k}
            type="button"
            disabled={saving}
            onClick={() => onStav(k)}
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition hover:brightness-95 disabled:opacity-50 ${STAVY[k].chip}`}
          >
            {STAVY[k].label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Svislá dělící čára mezi částmi řádku. Tečkové oddělovače se v adminu nepoužívají. */
function Delic() {
  return <span aria-hidden className="inline-block h-3 w-px shrink-0 bg-slate-300" />;
}

export default function WebZmeny() {
  const [zaznamy, setZaznamy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const [otevrene, setOtevrene] = useState(null);   // id rozbaleného řádku
  const [upravovane, setUpravovane] = useState(null); // id řádku v režimu úprav
  const [koncept, setKoncept] = useState(null);       // rozpracovaná kopie záznamu
  const [confirmDel, setConfirmDel] = useState(null);

  const [hledej, setHledej] = useState("");
  const [filtrTyp, setFiltrTyp] = useState("vse");
  const [filtrStav, setFiltrStav] = useState("vse");

  const detailRefs = useRef({});
  const [katalog, setKatalog] = useState([{ klic: "", label: "Nevybráno" }]);

  // Katalog sledovatelných čísel. Drží ho backend, ať UI nemusí znát SQL.
  useEffect(() => {
    apiFetch("/api/web-zmeny-cisla?katalog=1")
      .then((r) => r.json())
      .then((j) => { if (j.ok && Array.isArray(j.katalog)) setKatalog(j.katalog); })
      .catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    setErr(null);
    apiFetch("/api/web-zmeny")
      .then((r) => r.json())
      .then((j) => {
        if (j.ok) {
          setZaznamy(j.zaznamy || []);
          setUpdatedAt(j.updatedAt || null);
        } else {
          setErr(j.error || "Chyba načítání");
        }
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Po rozbalení plynule doskrolovat k obsahu, ať je vidět, co se otevřelo.
  useEffect(() => {
    if (!otevrene) return;
    const el = detailRefs.current[otevrene];
    if (el && el.scrollIntoView) {
      const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "nearest" }), 60);
      return () => clearTimeout(t);
    }
  }, [otevrene, upravovane]);

  const prepnout = (id) => {
    if (upravovane) return; // rozdělaná editace se klikem jinam neztratí
    setOtevrene((p) => (p === id ? null : id));
  };

  const zacitUpravu = (z) => {
    setKoncept({ ...z });
    setUpravovane(z.id);
    setOtevrene(z.id);
  };

  const zrusitUpravu = () => {
    setUpravovane(null);
    setKoncept(null);
    setConfirmDel(null);
    setErr(null);
  };

  /** Uloží celé pole naráz, backend přepisuje dokument. */
  const uloz = (novaData, poUlozeni) => {
    setSaving(true);
    setErr(null);
    apiFetch("/api/web-zmeny", { method: "POST", body: JSON.stringify({ zaznamy: novaData }) })
      .then((r) => r.json())
      .then((j) => {
        if (j.ok) {
          setZaznamy(j.zaznamy || novaData);
          setUpdatedAt(j.updatedAt || null);
          if (poUlozeni) poUlozeni();
        } else {
          setErr(j.error || "Uložení selhalo");
        }
      })
      .catch((e) => setErr(e.message))
      .finally(() => setSaving(false));
  };

  const ulozKoncept = () => {
    if (!koncept) return;
    const existuje = zaznamy.some((z) => z.id === koncept.id);
    const nova = existuje
      ? zaznamy.map((z) => (z.id === koncept.id ? koncept : z))
      : [koncept, ...zaznamy];
    uloz(nova, () => {
      setUpravovane(null);
      setKoncept(null);
    });
  };

  const smaz = (id) => {
    uloz(zaznamy.filter((z) => z.id !== id), () => {
      setConfirmDel(null);
      setUpravovane(null);
      setKoncept(null);
      setOtevrene(null);
    });
  };

  const novy = () => {
    const z = prazdnyZaznam();
    setKoncept(z);
    setUpravovane(z.id);
    setOtevrene(z.id);
    setZaznamy((p) => [z, ...p]); // jen v UI, uloží se až tlačítkem Uložit
  };

  const serazene = useMemo(
    () => [...zaznamy].sort((a, b) => String(b.datum || "").localeCompare(String(a.datum || ""))),
    [zaznamy]
  );

  const videt = useMemo(() => {
    const q = hledej.trim().toLowerCase();
    return serazene.filter((z) => {
      if (z.id === upravovane) return true; // rozdělaný záznam nesmí zmizet pod rukama
      if (filtrTyp !== "vse" && z.typ !== filtrTyp) return false;
      if (filtrStav !== "vse" && z.stav !== filtrStav) return false;
      if (!q) return true;
      return [z.nadpis, z.kde, z.co, z.proc, z.metrika, z.vysledek, z.poznamka]
        .join(" ").toLowerCase().includes(q);
    });
  }, [serazene, hledej, filtrTyp, filtrStav, upravovane]);

  const kCekani = serazene.filter((z) => z.stav === "ceka").length;
  const kVyhodnoceni = serazene.filter(jeCasVyhodnotit);

  if (loading && !zaznamy.length) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <Stranka>
      <GradientHeader
        variant="purple"
        icon={History}
        eyebrow="Web"
        title="Log změn webu"
        napoveda="Co se změnilo, co se od toho čekalo a jestli to zabralo."
      >
        <button
          type="button"
          onClick={novy}
          disabled={!!upravovane}
          className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-sm font-semibold text-brand-800 transition hover:bg-white/90 disabled:opacity-50 min-h-[44px] sm:min-h-0"
        >
          <Plus className="w-4 h-4" /> Nový záznam
        </button>
      </GradientHeader>

      {err && (
        <ErrorNote>{err}</ErrorNote>
      )}

      {/* Připomínka: tohle už je za termínem a pořád to nikdo nevyhodnotil. */}
      {kVyhodnoceni.length > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <CalendarClock className="mt-0.5 w-5 h-5 shrink-0 text-amber-600" />
          <div className="min-w-0 text-sm text-amber-900">
            <div className="font-semibold">
              {kVyhodnoceni.length === 1
                ? "Jedna změna je připravená k vyhodnocení"
                : `${kVyhodnoceni.length} změny čekají na vyhodnocení`}
            </div>
            <div className="mt-1 text-amber-800">{kVyhodnoceni.map((z) => z.nadpis).join(", ")}</div>
            <div className="mt-1.5 text-amber-700">Data najdeš v Analytice webu. Výsledek dopiš sem, i když vyjde blbě.</div>
          </div>
        </div>
      )}

      {/* Filtry */}
      <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={hledej}
            onChange={(e) => setHledej(e.target.value)}
            placeholder="Hledat v záznamech"
            className="field w-full pl-9 pr-3"
          />
        </div>
        <select
          value={filtrTyp}
          onChange={(e) => setFiltrTyp(e.target.value)}
          className="field w-auto"
        >
          <option value="vse">Všechny typy</option>
          {Object.entries(TYPY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select
          value={filtrStav}
          onChange={(e) => setFiltrStav(e.target.value)}
          className="field w-auto"
        >
          <option value="vse">Všechny stavy</option>
          {Object.entries(STAVY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <div className="flex shrink-0 items-center gap-2 px-1 text-xs">
          <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">{videt.length} z {serazene.length}</span>
          {kCekani > 0 && (
            <span className="rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 font-semibold text-amber-700">{kCekani} čeká</span>
          )}
        </div>
      </div>

      {/* Seznam */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {/* Hlavička sloupců, na mobilu se skrývá */}
        <div className="hidden items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:flex">
          <span className="w-4" />
          <span className="w-24 shrink-0">Datum</span>
          <span className="w-32 shrink-0">Typ</span>
          <span className="flex-1">Změna</span>
          <span className="w-40 shrink-0">Vyhodnotit</span>
          <span className="w-28 shrink-0 text-right">Stav</span>
        </div>

        {videt.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">Žádný záznam neodpovídá filtru.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {videt.map((z) => {
              const typ = TYPY[z.typ] || TYPY.zmena;
              const stav = STAVY[z.stav] || STAVY.ceka;
              const TypIkona = typ.icon;
              const cas = jeCasVyhodnotit(z);
              const rozbaleno = otevrene === z.id;
              const edituje = upravovane === z.id;
              const d = edituje ? koncept : z;

              return (
                <div
                  key={z.id}
                  ref={(el) => { detailRefs.current[z.id] = el; }}
                  className={`relative scroll-mt-24 ${rozbaleno ? "bg-slate-50/60" : ""}`}
                >
                  <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${typ.pruh}`} />

                  {/* ŘÁDEK */}
                  <button
                    type="button"
                    onClick={() => prepnout(z.id)}
                    className="flex w-full items-center gap-3 px-4 py-3 pl-5 text-left transition hover:bg-slate-50"
                  >
                    <span className="shrink-0 text-slate-400">
                      {rozbaleno ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>

                    <span className="w-24 shrink-0 text-sm font-semibold tabular-nums text-slate-700">{fmtDatum(d.datum)}</span>

                    <span className={`hidden w-32 shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold sm:inline-flex ${typ.chip}`}>
                      <TypIkona className="w-3.5 h-3.5" /> {typ.label}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-slate-900">{d.nadpis || "(bez nadpisu)"}</span>
                      {d.kde && (
                        <span className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                          <MapPin className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                          <span className="truncate">{d.kde}</span>
                        </span>
                      )}
                    </span>

                    <span className="hidden w-40 shrink-0 items-center gap-2 text-xs text-slate-500 sm:flex">
                      {d.vyhodnotitOd ? (
                        <>
                          <CalendarClock className={`w-3.5 h-3.5 ${cas ? "text-amber-600" : "text-slate-400"}`} />
                          <span className={cas ? "font-semibold text-amber-700" : ""}>{fmtDatum(d.vyhodnotitOd)}</span>
                        </>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </span>

                    <span className="flex w-28 shrink-0 justify-end">
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${cas ? "border-amber-300 bg-amber-100 text-amber-800" : stav.chip}`}>
                        {cas ? "Je čas" : stav.label}
                      </span>
                    </span>
                  </button>

                  {/* DETAIL */}
                  {rozbaleno && (
                    <div className="border-t border-slate-200 bg-white px-4 py-4 pl-5">
                      {!edituje ? (
                        <div className="space-y-3">
                          <div className="grid gap-3 sm:grid-cols-2">
                            {d.co && (
                              <div className="sm:col-span-2">
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Co se změnilo</div>
                                <p className="mt-1 text-sm leading-relaxed text-slate-800">{d.co}</p>
                              </div>
                            )}
                            {d.proc && (
                              <div className="sm:col-span-2">
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Proč</div>
                                <p className="mt-1 text-sm leading-relaxed text-slate-700">{d.proc}</p>
                              </div>
                            )}
                          </div>

                          {d.stav === "ceka" && (
                            <CislaZmeny
                              zaznam={d}
                              saving={saving}
                              onStav={(novy) => uloz(zaznamy.map((x) => (x.id === z.id ? { ...x, stav: novy } : x)))}
                            />
                          )}

                          {(d.metrika || d.vyhodnotitOd) && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl bg-slate-50 px-3.5 py-2.5">
                              {d.metrika && (
                                <span className="flex min-w-0 items-start gap-1.5 text-sm text-slate-700">
                                  <Target className="mt-0.5 w-4 h-4 shrink-0 text-brand-500" />
                                  <span>{d.metrika}</span>
                                </span>
                              )}
                              {d.metrika && d.vyhodnotitOd && <Delic />}
                              {d.vyhodnotitOd && (
                                <span className="flex shrink-0 items-center gap-1.5 text-sm text-slate-600">
                                  <CalendarClock className="w-4 h-4 text-slate-400" />
                                  Vyhodnotit od {fmtDatum(d.vyhodnotitOd)}
                                </span>
                              )}
                            </div>
                          )}

                          {d.vysledek && (
                            <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-900">
                              <span className="font-semibold">Výsledek: </span>{d.vysledek}
                            </p>
                          )}

                          {d.poznamka && (
                            <p className="text-xs leading-relaxed text-slate-500">
                              <span className="font-semibold uppercase tracking-wide text-slate-400">Pozn.: </span>{d.poznamka}
                            </p>
                          )}

                          <div className="flex flex-wrap items-center gap-2 pt-1">
                            <button
                              type="button"
                              onClick={() => zacitUpravu(z)}
                              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                            >
                              <Pencil className="w-3.5 h-3.5" /> Upravit
                            </button>
                            {confirmDel === z.id ? (
                              <span className="inline-flex items-center gap-2 text-sm">
                                <span className="text-slate-500">Opravdu smazat?</span>
                                <button type="button" onClick={() => smaz(z.id)} disabled={saving} className="btn btn-danger">Ano, smazat</button>
                                <button type="button" onClick={() => setConfirmDel(null)} className="rounded-xl bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200">Zpět</button>
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setConfirmDel(z.id)}
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 hover:text-rose-800"
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Smazat
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="grid gap-3 sm:grid-cols-2">
                            {POLE.map((f) => (
                              <div key={f.key} className={f.type === "textarea" || f.sirka === "full" ? "sm:col-span-2" : ""}>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{f.label}</label>
                                {f.type === "textarea" ? (
                                  <textarea
                                    value={koncept[f.key] || ""}
                                    onChange={(e) => setKoncept((k) => ({ ...k, [f.key]: e.target.value }))}
                                    rows={2}
                                    className="field w-full resize-y"
                                  />
                                ) : f.type === "metrika" ? (
                                  <select
                                    value={koncept[f.key] || ""}
                                    onChange={(e) => setKoncept((k) => ({ ...k, [f.key]: e.target.value }))}
                                    className="field w-full"
                                  >
                                    {katalog.map((m) => <option key={m.klic} value={m.klic}>{m.label}</option>)}
                                  </select>
                                ) : f.type === "select" ? (
                                  <select
                                    value={koncept[f.key] || f.options[0]}
                                    onChange={(e) => setKoncept((k) => ({ ...k, [f.key]: e.target.value }))}
                                    className="field w-full"
                                  >
                                    {f.options.map((o) => <option key={o} value={o}>{f.popis(o)}</option>)}
                                  </select>
                                ) : (
                                  <input
                                    type={f.type === "date" ? "date" : "text"}
                                    value={koncept[f.key] || ""}
                                    onChange={(e) => setKoncept((k) => ({ ...k, [f.key]: e.target.value }))}
                                    className="field w-full"
                                  />
                                )}
                              </div>
                            ))}
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={ulozKoncept}
                              disabled={saving}
                              className="btn btn-primary"
                            >
                              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Uložit
                            </button>
                            <button
                              type="button"
                              onClick={() => { zrusitUpravu(); load(); }}
                              disabled={saving}
                              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-3.5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
                            >
                              <X className="w-4 h-4" /> Zrušit
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Jak log používat. Bez pravidel se z něj do měsíce stane seznam přání. */}
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
        <div className="text-sm font-semibold text-slate-900">Jak log používat</div>
        <ul className="mt-2 space-y-1.5 text-sm text-slate-600">
          <li>Každá změna, která může hnout s čísly, dostane záznam <span className="font-semibold text-slate-700">v den nasazení</span>.</li>
          <li>Vždycky se dopíše <span className="font-semibold text-slate-700">jedno konkrétní číslo</span>, které se má zlepšit. Když se nedá pojmenovat, je to nejspíš kosmetika a do logu nepatří.</li>
          <li><span className="font-semibold text-slate-700">Vyhodnotit od</span> = datum, kdy budou data dost velká. Pravidlo: minimálně 100 návštěv dané stránky po nasazení, a ne dřív než za 14 dní.</li>
          <li>Na jednu stránku ideálně <span className="font-semibold text-slate-700">jedna změna za den</span>. Dvě naráz znamenají, že se efekt nedá rozdělit, a musí to být v poznámce.</li>
          <li><span className="font-semibold text-slate-700">Výsledek</span> se dopisuje zpětně, i když vyjde blbě. Záznam, který se nikdy nevyhodnotí, je k ničemu.</li>
        </ul>
      </div>
    </Stranka>
  );
}
